import React, { useState } from 'react';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { SettingsSection } from './SettingsLayout';
import { useSettings } from './useSettings';
import { useAuth } from '../../contexts/AuthContext';
import api, { formatApiError } from '../../api';
import { toast } from 'sonner';
import { Save, Eye, Send, Play, Clock, Mail } from 'lucide-react';

const PLACEHOLDERS = [
    ['{client_name}', 'Client company name'],
    ['{invoice_table}', 'Auto-built table: invoice no, due date, days late, amount, pay links'],
    ['{total_due}', 'Total outstanding across their invoices'],
    ['{days_late}', 'Days late of the oldest invoice'],
    ['{count}', 'Number of unpaid invoices'],
];

const GBP = (v) => `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RemindersSettings() {
    const { settings, update, save, saving } = useSettings();
    const { can } = useAuth();
    const readOnly = !can('settings', 'edit');

    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [running, setRunning] = useState(false);
    const [testTo, setTestTo] = useState('');
    const [testing, setTesting] = useState(false);
    const [history, setHistory] = useState(null);
    const [expanded, setExpanded] = useState(null);

    if (!settings) return <div className="text-[color:var(--bm-text-muted)]">Loading…</div>;

    const daysStr = Array.isArray(settings.reminder_days)
        ? settings.reminder_days.join(', ')
        : (settings.reminder_days || '');

    const REMINDER_KEYS = ['reminders_enabled', 'reminder_days', 'reminder_include_part_paid',
        'reminder_cc', 'reminder_email_subject', 'reminder_email_body'];

    const doSave = async () => { await save(REMINDER_KEYS); };

    const doPreview = async () => {
        setPreviewing(true);
        try {
            const r = await api.get('/reminders/preview');
            setPreview(r.data);
            if (!r.data.clients?.length) toast.message('No clients are due a reminder right now.');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Preview failed'); }
        finally { setPreviewing(false); }
    };

    const doRunNow = async () => {
        if (!window.confirm('Send all currently-due reminder emails now?')) return;
        setRunning(true);
        try {
            const r = await api.post('/reminders/run-now');
            if (r.data.skipped) toast.error(`Not sent: ${r.data.skipped.replace(/_/g, ' ')}`);
            else toast.success(`Sent ${r.data.sent || 0} reminder(s)${r.data.failed ? `, ${r.data.failed} failed` : ''}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Run failed'); }
        finally { setRunning(false); }
    };

    const doTest = async () => {
        if (!testTo.trim()) return toast.error('Enter an email address');
        setTesting(true);
        try {
            await api.post('/reminders/test-email', { to: testTo.trim() });
            toast.success(`Test reminder sent to ${testTo.trim()}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Test failed'); }
        finally { setTesting(false); }
    };

    const loadHistory = async () => {
        try {
            const r = await api.get('/reminders/history', { params: { limit: 50 } });
            setHistory(r.data.items || []);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };

    return (
        <div data-testid="reminders-settings-page">
            <fieldset disabled={readOnly} className={readOnly ? 'opacity-70 pointer-events-none' : ''}>
                <SettingsSection title="Automated payment reminders"
                    description="Chase unpaid invoices automatically. One email per client lists all their unpaid invoices with pay links. Runs daily; each invoice is only chased once per milestone.">
                    <div className="sm:col-span-2 flex items-center justify-between rounded-md border border-[color:var(--bm-border)] px-4 py-3">
                        <div>
                            <div className="text-[13px] font-medium text-[color:var(--bm-text)]">Enable automated reminders</div>
                            <div className="text-[11px] text-[color:var(--bm-text-subtle)]">When off, nothing is sent automatically (you can still use “Send reminders now”).</div>
                        </div>
                        <Switch checked={!!settings.reminders_enabled} onCheckedChange={v => update('reminders_enabled', v)} data-testid="reminders-enabled-switch" />
                    </div>
                    <div>
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Reminder days (days overdue)</Label>
                        <Input value={daysStr} onChange={e => update('reminder_days', e.target.value)}
                               className="mt-1.5 h-10" placeholder="7, 14, 30" data-testid="reminders-days-input" />
                        <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1">Comma-separated. An email fires when an invoice reaches each milestone.</div>
                    </div>
                    <div>
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">CC (comma-separated)</Label>
                        <Input value={settings.reminder_cc || ''} onChange={e => update('reminder_cc', e.target.value)}
                               className="mt-1.5 h-10" placeholder="you@beyondmeasure.global" data-testid="reminders-cc-input" />
                        <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1">CC yourself on every reminder sent.</div>
                    </div>
                    <div className="sm:col-span-2 flex items-center justify-between rounded-md border border-[color:var(--bm-border)] px-4 py-3">
                        <div>
                            <div className="text-[13px] font-medium text-[color:var(--bm-text)]">Also chase partially-paid invoices</div>
                            <div className="text-[11px] text-[color:var(--bm-text-subtle)]">Include invoices with a remaining balance, not just fully-unpaid ones.</div>
                        </div>
                        <Switch checked={settings.reminder_include_part_paid !== false} onCheckedChange={v => update('reminder_include_part_paid', v)} data-testid="reminders-partpaid-switch" />
                    </div>
                </SettingsSection>

                <SettingsSection title="Email content" wide
                    description="Recipients = all client contacts (falls back to the client's main email). Use placeholders below — they're filled per client when sent.">
                    <div className="sm:col-span-2">
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Subject</Label>
                        <Input value={settings.reminder_email_subject || ''} onChange={e => update('reminder_email_subject', e.target.value)}
                               className="mt-1.5 h-10" data-testid="reminders-subject-input" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Body</Label>
                        <Textarea value={settings.reminder_email_body || ''} onChange={e => update('reminder_email_body', e.target.value)}
                                  rows={12} className="mt-1.5 font-mono text-[12.5px]" data-testid="reminders-body-input" />
                    </div>
                    <div className="sm:col-span-2 rounded-md bg-[color:var(--bm-bg-mute)] p-3">
                        <div className="text-[11px] font-semibold text-[color:var(--bm-text-muted)] uppercase tracking-wide mb-1.5">Placeholders</div>
                        <div className="grid sm:grid-cols-2 gap-1">
                            {PLACEHOLDERS.map(([tok, desc]) => (
                                <div key={tok} className="text-[11.5px]">
                                    <code className="text-[color:var(--bm-primary)] font-semibold">{tok}</code>
                                    <span className="text-[color:var(--bm-text-subtle)]"> — {desc}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </SettingsSection>

                {!readOnly && (
                    <div className="flex flex-wrap gap-2 max-w-4xl mb-5">
                        <Button onClick={doSave} disabled={saving} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="reminders-save-btn">
                            <Save size={15} className="mr-1.5" /> {saving ? 'Saving…' : 'Save reminders'}
                        </Button>
                        <Button onClick={doPreview} disabled={previewing} variant="outline" className="h-9" data-testid="reminders-preview-btn">
                            <Eye size={15} className="mr-1.5" /> {previewing ? 'Loading…' : 'Preview who’s due'}
                        </Button>
                        <Button onClick={doRunNow} disabled={running} variant="outline" className="h-9" data-testid="reminders-run-btn">
                            <Play size={15} className="mr-1.5" /> {running ? 'Sending…' : 'Send reminders now'}
                        </Button>
                    </div>
                )}
            </fieldset>

            <SettingsSection title="Send a test email"
                description="Sends a sample reminder (using a real due client if one exists, otherwise mock data) to the address below via your connected Microsoft account.">
                <div className="sm:col-span-2 flex gap-2">
                    <Input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@beyondmeasure.global"
                           className="h-10 flex-1" data-testid="reminders-test-email-input" />
                    <Button onClick={doTest} disabled={testing} className="h-10" style={{ background: 'var(--bm-primary)' }} data-testid="reminders-test-send-btn">
                        <Send size={15} className="mr-1.5" /> {testing ? 'Sending…' : 'Send test'}
                    </Button>
                </div>
            </SettingsSection>

            {preview && (
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 mb-5 max-w-4xl" data-testid="reminders-preview-panel">
                    <div className="font-display text-[15px] font-semibold mb-1">Due today — {preview.count} client(s)</div>
                    <div className="text-[12px] text-[color:var(--bm-text-muted)] mb-4">
                        Milestones: {preview.reminder_days?.join(', ')} days · {preview.enabled ? 'Automation ON' : 'Automation OFF'}
                        {preview.cc?.length ? ` · CC ${preview.cc.join(', ')}` : ''}
                    </div>
                    {preview.clients.length === 0 && <div className="text-[13px] text-[color:var(--bm-text-muted)]">No clients are due a reminder right now.</div>}
                    <div className="space-y-2">
                        {preview.clients.map(c => (
                            <div key={c.client_id} className="border border-[color:var(--bm-border)] rounded-lg" data-testid={`reminders-preview-client-${c.client_id}`}>
                                <button onClick={() => setExpanded(expanded === c.client_id ? null : c.client_id)}
                                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[color:var(--bm-bg-mute)]">
                                    <div>
                                        <div className="text-[13px] font-medium text-[color:var(--bm-text)]">{c.client_name}</div>
                                        <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">
                                            {c.invoice_count} invoice(s) · {c.max_days_late} days late · {c.recipients.length ? `to ${c.recipients.join(', ')}` : 'no recipient email!'}
                                        </div>
                                    </div>
                                    <div className="text-[13px] font-semibold text-[color:var(--bm-primary)]">{GBP(c.total_due)}</div>
                                </button>
                                {expanded === c.client_id && (
                                    <div className="border-t border-[color:var(--bm-border)] p-4">
                                        <div className="text-[12px] font-medium mb-2">Subject: <span className="text-[color:var(--bm-text-muted)]">{c.subject}</span></div>
                                        <div className="border border-[color:var(--bm-border)] rounded overflow-hidden" dangerouslySetInnerHTML={{ __html: c.html }} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 max-w-4xl" data-testid="reminders-history-panel">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-display text-[15px] font-semibold flex items-center gap-2"><Clock size={15} /> Sent history</div>
                    <Button onClick={loadHistory} variant="outline" size="sm" data-testid="reminders-history-load-btn">Load history</Button>
                </div>
                {history == null ? (
                    <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Click “Load history” to view sent reminders.</div>
                ) : history.length === 0 ? (
                    <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">No reminders sent yet.</div>
                ) : (
                    <div className="space-y-1">
                        {history.map(h => (
                            <div key={h.id} className="flex items-center justify-between text-[12.5px] py-2 px-2 rounded hover:bg-[color:var(--bm-bg-mute)]" data-testid={`reminders-history-${h.id}`}>
                                <div className="flex items-center gap-2">
                                    <Mail size={13} className={h.status === 'sent' ? 'text-[color:var(--bm-accent-600)]' : 'text-red-500'} />
                                    <span className="font-medium">{h.client_name}</span>
                                    <span className="text-[color:var(--bm-text-subtle)]">· {(h.invoice_numbers || []).join(', ')}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-[color:var(--bm-text-muted)]">{GBP(h.total_due)}</span>
                                    <span className="text-[color:var(--bm-text-subtle)]">{(h.sent_at || h.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                                    <span className={`bm-pill text-[10px] border ${h.status === 'sent' ? 'text-[color:var(--bm-accent-700)]' : 'text-red-600 border-red-200'}`}>
                                        {h.status === 'sent' ? 'Sent' : (h.error || 'Failed')}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
